import { motion } from "framer-motion";
import { ArrowUpRight, Clock, MapPin } from "lucide-react";
import { RESTAURANTS } from "@/data/restaurants";
import { trackClick } from "@/lib/tracking";

const EASE = [0.16, 1, 0.3, 1];

function Card({ r, i }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: EASE, delay: (i % 2) * 0.1 }}
      className="group"
      data-testid={`restaurant-card-${r.id}`}
    >
      <div className="relative overflow-hidden border border-white/10">
        <div className="aspect-[4/5] overflow-hidden">
          <img
            src={r.image}
            alt={r.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
        <span className="font-accent absolute right-5 top-4 text-5xl italic text-gold/80">
          {r.index}
        </span>
        <div className="absolute bottom-5 left-5 right-5">
          <p className="text-[11px] uppercase tracking-[0.25em] text-gold">
            {r.tag}
          </p>
          {r.logo ? (
            <img
              src={r.logo}
              alt={r.name}
              className={`mt-2 w-auto max-w-[80%] object-contain object-left-bottom drop-shadow-[0_2px_12px_rgba(0,0,0,0.65)] ${r.logoClass}`}
              data-testid={`logo-${r.id}`}
            />
          ) : (
            <h3 className="font-display mt-1 text-3xl text-bone md:text-4xl">
              {r.name}
            </h3>
          )}
        </div>
      </div>
      <div className="border border-t-0 border-white/10 bg-coal p-6 md:p-8">
        <p className="text-sm leading-relaxed text-zinc-300">{r.description}</p>
        <div className="mt-5 space-y-2 text-sm text-zinc-400">
          <p className="flex items-start gap-2">
            <MapPin size={15} className="mt-0.5 shrink-0 text-gold" />
            {r.address}
          </p>
          <p className="flex items-start gap-2">
            <Clock size={15} className="mt-0.5 shrink-0 text-gold" />
            {r.hours}
          </p>
        </div>
        <div className="mt-6">
          <a
            href={r.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`reserve-${r.id}`}
            onClick={() => trackClick(r.id)}
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 text-xs uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink"
          >
            {r.ctaLabel || "Réserver"} <ArrowUpRight size={15} />
          </a>
          {r.note && (
            <p
              className="mt-2 text-xs tracking-wide text-zinc-500"
              data-testid={`note-${r.id}`}
            >
              {r.note}
            </p>
          )}
        </div>
      </div>
    </motion.article>
  );
}

export default function Restaurants() {
  return (
    <section
      id="restaurants"
      data-testid="restaurants-section"
      className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36"
    >
      <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="text-xs uppercase tracking-[0.3em] text-gold/90">
            Nos maisons
          </p>
          <h2 className="font-display mt-4 text-4xl leading-tight tracking-tight text-bone sm:text-5xl lg:text-6xl">
            Cinq maisons,
            <br />
            une même promesse.
          </h2>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          className="max-w-sm text-sm leading-relaxed text-zinc-400"
        >
          Cinq adresses, cinq ambiances — les maisons toulousaines du
          groupe All For You, ouvertes pendant le couvre-feu.
        </motion.p>
      </div>

      <div className="mt-16 grid gap-12 md:mt-24 md:grid-cols-2 md:gap-x-14 md:gap-y-24">
        {RESTAURANTS.map((r, i) => (
          <div key={r.id} className={i % 2 === 1 ? "md:mt-24" : ""}>
            <Card r={r} i={i} />
          </div>
        ))}
      </div>
    </section>
  );
}
